import { useEffect, useState, useCallback } from 'react';
import type { HTMLAttributes } from 'react';
import type { DateRange } from 'react-day-picker';
import { format } from 'date-fns/format';
import { addMonths } from 'date-fns/addMonths';
import { setHours } from 'date-fns/setHours';
import { setMinutes } from 'date-fns/setMinutes';
import { de as LocaleDe } from 'date-fns/locale/de';
import { fr as LocaleFr } from 'date-fns/locale/fr';
import { es as LocaleEs } from 'date-fns/locale/es';
import { it as LocaleIt } from 'date-fns/locale/it';
import { nl as LocaleNl } from 'date-fns/locale/nl';
import { enUS as LocaleEn } from 'date-fns/locale/en-US';
import { tr as LocaleTr } from 'date-fns/locale/tr';

import { String, Theme } from '@silvertours/front-shared';
import { useLocale } from '../../Runtime';
import { DatePicker } from './DatePicker';

const INITIAL_MONTHS_MOBILE_CALENDAR = 2;
const MAXIMUM_MONTHS_IN_CALENDAR = 24;

export const addTimeToDate = (date: Date, hours: number, minutes: number) =>
  setMinutes(setHours(date, hours), minutes);

type CalendarProps = {
  dayCount?: number;
  closeText?: string;
  hasManuallySetTimes: boolean;
  vertical?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  onChange?: (dateRange: DateRange, shouldClose?: boolean) => void;
  onClose?: () => void;
} & DateRange &
  Pick<HTMLAttributes<HTMLDivElement>, 'className'>;

export const Calendar = ({
  from,
  to,
  vertical = false,
  hasManuallySetTimes,
  showHeader,
  showFooter,
  dayCount,
  closeText,
  className,
  onChange,
  onClose,
}: CalendarProps) => {
  const { language } = useLocale();
  const isMobileTouchDevice = Theme.useIsMobileTouchDevice();

  const dateLocale = {
    de: LocaleDe,
    fr: LocaleFr,
    es: LocaleEs,
    it: LocaleIt,
    nl: LocaleNl,
    en: LocaleEn,
    tu: LocaleTr,
  }[language as keyof typeof String.dateFormats];

  const selectedDates = { from, to };

  const localDateFormats =
    String.dateFormats[language as keyof typeof String.dateFormats];

  const handleOnDatesChange = (dateRange: DateRange, shouldClose?: boolean) => {
    let newDateRange;
    if (
      dateRange.from &&
      dateRange.to &&
      selectedDates.from &&
      selectedDates.to
    ) {
      const isSameDay =
        format(dateRange.from, String.isoDateTime).split('T')[0] ===
        format(dateRange.to, String.isoDateTime).split('T')[0];

      if (isSameDay) {
        // Same-day selection: use 10:00-18:00 unless user manually set times
        if (hasManuallySetTimes || !isMobileTouchDevice) {
          newDateRange = {
            from: addTimeToDate(
              dateRange.from,
              selectedDates.from.getHours(),
              selectedDates.from.getMinutes(),
            ),
            to: addTimeToDate(
              dateRange.to,
              selectedDates.to.getHours(),
              selectedDates.to.getMinutes(),
            ),
          };
        } else {
          newDateRange = {
            from: addTimeToDate(dateRange.from, 10, 0),
            to: addTimeToDate(dateRange.to, 17, 0),
          };
        }
      } else if (hasManuallySetTimes) {
        // Different days: preserve times if manually set, otherwise reset to defaults
        newDateRange = {
          from: addTimeToDate(
            dateRange.from,
            selectedDates.from.getHours(),
            selectedDates.from.getMinutes(),
          ),
          to: addTimeToDate(
            dateRange.to,
            selectedDates.to.getHours(),
            selectedDates.to.getMinutes(),
          ),
        };
      } else {
        const hours = language === 'fr' ? 11 : 12;
        newDateRange = {
          from: addTimeToDate(dateRange.from, hours, 0),
          to: addTimeToDate(dateRange.to, 9, 0),
        };
      }

      if (typeof onChange !== 'undefined') {
        onChange(newDateRange, shouldClose);
      }
    }
  };

  const getDefaultMonth = () => {
    if (vertical || !selectedDates.from) {
      return undefined;
    }
    return selectedDates.from;
  };

  const getInitialNumberOfMonths = () => {
    if (vertical) {
      return INITIAL_MONTHS_MOBILE_CALENDAR;
    }
    return 2;
  };

  const [numberOfMonths, setNumberOfMonths] = useState(
    getInitialNumberOfMonths(),
  );

  const addMoreMonths = useCallback(() => {
    setNumberOfMonths(Math.min(numberOfMonths + 4, MAXIMUM_MONTHS_IN_CALENDAR));
  }, [numberOfMonths]);

  useEffect(() => {
    // Reset months on viewport change
    setNumberOfMonths(getInitialNumberOfMonths());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vertical]);

  const now = new Date();
  const calendarRange = {
    start: now,
    end: addMonths(now, MAXIMUM_MONTHS_IN_CALENDAR),
  };

  return (
    <DatePicker
      mode="range"
      disabled={{ before: new Date() }}
      numberOfMonths={numberOfMonths}
      startMonth={calendarRange.start}
      endMonth={calendarRange.end}
      from={selectedDates.from ?? undefined}
      to={selectedDates.to ?? undefined}
      locale={dateLocale}
      defaultMonth={getDefaultMonth()}
      scrollToSelectedMonth={vertical}
      dateFormat={
        vertical ? localDateFormats.dayDateLongYear : localDateFormats.dayDate
      }
      vertical={vertical}
      autoFocus={!vertical} // eslint-disable-line jsx-a11y/no-autofocus
      dayCount={dayCount}
      closeText={closeText}
      showHeader={!!showHeader}
      showFooter={!!showFooter && !!dayCount}
      className={className}
      onScrollToBottom={addMoreMonths}
      onDatesChange={handleOnDatesChange}
      onClose={onClose}
    />
  );
};
